<template>
  <el-button v-if="type === 'text'" type="text" @click="onClick">
    <slot></slot>
  </el-button>
  <el-button v-else class="au-button" :class="{ 'rgb': rgbType }" :size="size" :icon="icon" :loading="loading"
    :disabled="disabled" :circle="circle" :style="style" @click="onClick" @focus.native="is_hover_or_focus = true"
    @blur.native="is_hover_or_focus = false" @mouseenter.native="is_hover_or_focus = true"
    @mouseleave.native="is_hover_or_focus = false" @mousedown.native="is_active = true"
    @mouseup.native="is_active = false" @keyup.prevent.native>
    <slot></slot>
  </el-button>
</template>

<script>
/**
 * @description 对 el-button 进行预设封装
 * @created 2023/10/04 17:25:27
 * @property {Array} rgb 按钮颜色
 * @property {String} type 按钮类型
 * @property {String} size 按钮尺寸
 * @property {String} icon 按钮图标
 * @property {Boolean} loading 是否加载中
 * @property {Boolean} disabled 是否禁用
 * @property {Boolean} circle 是否圆形按钮
 */
// 常量
import * as COLOR from "@/constants/color"
export default {
  name: "AuButton",
  props: {
    rgb: Array,
    type: String,
    size: String,
    icon: {
      type: String,
      default: ""
    },
    loading: Boolean,
    disabled: Boolean,
    circle: Boolean
  },
  data() {
    return {
      is_hover_or_focus: false,
      is_active: false
    }
  },
  computed: {
    rgbType() {
      return this.type && COLOR[`COLOR_${this.type.toUpperCase()}`] || this.rgb
    },
    originStyle() {
      if (this.rgbType) {
        const rgbString = this.rgbType.join(", ")
        if (this.type === "text") {
          return `color: rgb(${rgbString});`
        }
        return `border-color: rgb(${rgbString});background-color: rgb(${rgbString});`
      }
      return ""
    },
    hoverAndFocusStyle() {
      if (this.rgbType) {
        const rgbString = this.rgbType.join(", ")
        if (this.type === "text") {
          return `color: rgba(${rgbString}, 0.8);`
        }
        return `border-color: rgba(${rgbString}, 0.1);background-color: rgba(${rgbString}, 0.8);`
      }
      return ""
    },
    activeStyle() {
      if (this.rgbType) {
        const rgbString = this.increaseColorDepth(this.rgbType, 0.9).join(", ")
        if (this.type === "text") {
          return `color: rgb(${rgbString});`
        }
        return `border-color: rgb(${rgbString});background-color: rgb(${rgbString});`
      }
      return ""
    },
    disabledStyle() {
      if (this.rgbType) {
        const rgbString = this.rgbType.join(", ")
        if (this.type === "text") {
          return `color: rgba(${rgbString}, 0.4);`
        }
        return `background-color: rgba(${rgbString}, 0.4);`
      }
      return ""
    },
    style() {
      if (this.disabled) {
        return this.disabledStyle
      } else {
        if (this.is_active) {
          return this.activeStyle
        } else if (this.is_hover_or_focus) {
          return this.hoverAndFocusStyle
        } else {
          return this.originStyle
        }
      }
    }
  },
  methods: {
    // 点击事件
    onClick(e) {
      this.$emit("click", e)
    },
    // 增加颜色的深度
    increaseColorDepth(rgbArray, factor) {
      // factor：大于 1 变亮，小于 1 变暗
      const increasedRgbArray = rgbArray.map(component => {
        // 线性插值
        const increasedComponent = component * factor
        // 指数插值
        // const increasedComponent = Math.pow(component, factor)
        // 限制每个分量的范围在0-255之间
        return parseInt(Math.min(Math.max(0, increasedComponent), 255))
      })
      return increasedRgbArray
    }
  }
}
</script>

<style lang="scss" scoped>
.au-button {
  &:hover {
    color: #606266;
    background: #FFF;
    border: 1px solid #DCDFE6;
  }

  &:focus {
    color: #606266;
    background: #FFF;
    border: 1px solid #DCDFE6;
  }

  &:active {
    background: #e6e6e6;
  }

  &.rgb {
    color: #fff;

    &.is-disabled {
      border-color: transparent;
    }

    &.el-button--text {
      border-color: transparent;
      background-color: transparent;
    }
  }
}
</style>
